import CheckCircleIcon from 'mdi-react/CheckCircleIcon'
import HelpCircleIcon from 'mdi-react/HelpCircleIcon'
import WarningIcon from 'mdi-react/WarningIcon'
import React from 'react'
import { AssessmentResult } from 'scenes/Dashboard/data/analyses/analyses.types'
import { makeStyles } from 'tss-react/mui'

import { TableRow, Theme, Typography } from '@mui/material'

import RowCell, { RowType } from '../../../../../RowCell'
import TernarySelector, { SelectableOption } from './components/TernarySelector'

type AssessmentRowProps = {
  headerTitle?: string
  analysisScore?: number
  label?: string
  value?: any
  isNegativeAssessment?: boolean
}

export const AssessmentRow = React.memo(
  ({ headerTitle, analysisScore, label, value, isNegativeAssessment = false }: AssessmentRowProps) => {
    const { classes } = useStyles()

    const rowType = !label ? RowType.header : RowType.default

    const checkCell = (
      <RowCell
        className={classes.checkLabelCell}
        rowType={rowType}
        contentByRowType={{
          [RowType.header]: headerTitle,
          [RowType.default]: (
            <>
              <Typography variant="body2" dangerouslySetInnerHTML={{ __html: label || '' }} />
            </>
          ),
        }}
      />
    )

    const assessmentCell = (
      <RowCell
        className={classes.valueCell}
        centered
        rowType={rowType}
        contentByRowType={{
          [RowType.header]: `${analysisScore}%`,
          [RowType.default]: isNegativeAssessment ? (
            value === AssessmentResult.MET_VALUE ? (
              <WarningIcon className={classes.warningIcon} />
            ) : value === AssessmentResult.UNMET_VALUE ? (
              <CheckCircleIcon className={classes.checkIcon} />
            ) : (
              <HelpCircleIcon className={classes.questionIcon} />
            )
          ) : (
            <TernarySelector selectedOption={mapToSelectableOption(value)} />
          ),
        }}
      />
    )

    return (
      <TableRow key={value?.id}>
        {checkCell}
        {assessmentCell}
      </TableRow>
    )
  }
)

AssessmentRow.displayName = 'AssessmentRow'

const mapToSelectableOption = (assessmentValue?: AssessmentResult) => {
  if (assessmentValue === undefined) {
    return undefined
  }
  switch (assessmentValue) {
    case AssessmentResult.UNMET_VALUE:
      return SelectableOption.No
    case AssessmentResult.MET_VALUE:
      return SelectableOption.Yes
    default:
      return SelectableOption.Unknown
  }
}

const useStyles = makeStyles()(({ palette, spacing }: Theme) => ({
  checkLabelCell: {
    cursor: 'pointer',
  },
  valueCell: {
    width: 100,
  },
  checkIcon: {
    margin: 'auto',
    color: palette.extra.positive,
  },
  errorIcon: {
    marginRight: spacing(0.5),
    color: palette.extra.negative,
  },
  warningIcon: {
    margin: 'auto',
    color: palette.extra.negative,
  },
  questionIcon: {
    margin: 'auto',
    color: palette.extra.neutral,
  },
}))
